import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { Referrers } from '../../consts/defaultConfigValues/defaultConstants'
import usePrefillCartMutation from '../../graphQL/mutations/prefillCart'
import { IPrefillCartMutationResponse } from '../../graphQL/mutations/prefillCart/types'
import { ICartProduct, ICartProductInput } from '../../interfaces'
import { setSynchronizingCart } from '../../redux/slices/catalog'
import { onCartResponse } from '../../utils/onCartResponse'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import { generateDataDogContext } from '../../utils/dataDog'
import { fireGTMEvent } from '../../utils/fireGTMEvent'
import { parseAnalyticsItems } from '../../utils/getAnalyticsItems'

export type IAddPrefillCartItem = Pick<
  ICartProduct,
  'sku' | 'quantity' | 'package' | 'price' | 'discounts' | 'name'
>

interface AddPrefillCartArgs {
  items: IAddPrefillCartItem[]
  storefrontName: string
  sessionUid: string
  onCompleted?: (data: IPrefillCartMutationResponse) => void
  replaceCart?: boolean
  referrer?: Referrers
}

export const useAddPrefillCart = () => {
  const dispatch = useAppDispatch()
  const stateRedux = useAppSelector((state) => state)
  const currency = useAppSelector(
    (state) => state.defaultSlice.config?.options?.currencyPrefix || '',
  )

  /**
   * Handles onComplete callback from prefill cart mutation
   */
  const onCompletePrefillCartMutation = (
    data: IPrefillCartMutationResponse,
  ) => {
    if (data?.prefillCart) {
      onCartResponse({
        data: data.prefillCart,
        state: stateRedux,
        dispatch,
      })
    }
    dispatch(setSynchronizingCart(false))
  }

  /**
   * Handles onError callback from prefill cart mutation
   */
  const onErrorPrefillCartMutation = (error: ApolloError) => {
    dispatch(setSynchronizingCart(false))
    const context = generateDataDogContext({
      title: 'Prefill cart mutation error',
      extraInfo: { function: 'usePrefillCartMutation' },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }

  /**
   * Get prefillCart mutation hook
   */
  const [sendPrefillCart] = usePrefillCartMutation({
    onCompleted: onCompletePrefillCartMutation,
    onError: onErrorPrefillCartMutation,
  })

  /**
   * Adds prefill cart items to cart.
   *
   * Returns the mutation payload on success and `null` on failure, because a
   * caller cannot learn that any other way. Apollo's `useMutation` only
   * rejects when no `onError` is registered; with one — as above — it
   * resolves with `{ data: undefined, errors }` instead, so a `try`/`catch`
   * around this call never sees a failed mutation.
   */
  const addPrefillCart = async ({
    items,
    storefrontName,
    sessionUid,
    onCompleted,
    replaceCart,
    referrer = Referrers.PREFILL_CART,
  }: AddPrefillCartArgs) => {
    dispatch(setSynchronizingCart(true))
    const filteredItems = items.filter((item) => item.quantity > 0)
    fireGTMEvent('commerce-add_to_cart', {
      currency,
      value: filteredItems.reduce(
        (acc, { price, quantity }) => acc + price * quantity,
        0,
      ),
      items: filteredItems.map((item, index) =>
        parseAnalyticsItems(item, index),
      ),
    })
    const itemsToAdd = filteredItems.map<ICartProductInput>((item) => ({
      sku: item.sku,
      quantity: item.quantity,
      selectedPackage: item.package?.name,
    }))

    const response = await sendPrefillCart({
      variables: {
        storefrontName,
        sessionUid,
        items: itemsToAdd,
        replaceCart,
        referrer,
      },
    })
    if (response?.data) {
      onCompleted?.(response.data)
      return response.data
    }
    return null
  }

  return { addPrefillCart }
}
